import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus, 
  Crown, 
  Shield, 
  User,
  Mail,
  Trash2,
  Loader2,
  Building2,
  Clock,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";
import { organizationsAPI } from "@/lib/api";

const roleIcons = {
  owner: Crown,
  admin: Shield,
  member: User
};

const roleColors = {
  owner: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  admin: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  member: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};

const roleLabels = {
  owner: "Propietario",
  admin: "Administrador",
  member: "Miembro"
};

export default function TeamPage({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [invitationToCancel, setInvitationToCancel] = useState(null);
  
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "member"
  });
  
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    loadOrganization();
  }, []);

  const loadOrganization = async () => {
    try {
      const response = await organizationsAPI.getMyOrganization();
      setOrgData(response.data);
    } catch (error) {
      toast.error("Error al cargar la organización");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      toast.error("Ingresa un nombre para la organización");
      return;
    }
    
    setCreating(true);
    try {
      await organizationsAPI.create({ name: orgName });
      toast.success("Organización creada exitosamente");
      setShowCreateOrgDialog(false);
      setOrgName("");
      loadOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la organización");
    } finally {
      setCreating(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email) {
      toast.error("Ingresa un email");
      return;
    }
    
    setInviting(true);
    try {
      const response = await organizationsAPI.inviteUser(inviteForm);
      if (response.data.email_sent) {
        toast.success(`Invitación enviada a ${inviteForm.email}`);
      } else {
        toast.success("Invitación creada. El email no pudo ser enviado.", {
          description: "Comparte el link manualmente"
        });
      }
      setShowInviteDialog(false);
      setInviteForm({ email: "", role: "member" });
      loadOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar invitación");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;
    
    try {
      await organizationsAPI.removeMember(memberToDelete.id);
      toast.success("Miembro eliminado del equipo");
      setMemberToDelete(null);
      loadOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar miembro");
    }
  };

  const handleCancelInvitation = async () => {
    if (!invitationToCancel) return;
    
    try {
      await organizationsAPI.cancelInvitation(invitationToCancel.id);
      toast.success("Invitación cancelada");
      setInvitationToCancel(null);
      loadOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar invitación");
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await organizationsAPI.updateMemberRole(memberId, newRole);
      toast.success("Rol actualizado");
      loadOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar rol");
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#34C759]" />
        </div>
      </Layout>
    );
  }

  const canManage = orgData?.membership?.role === "owner" || orgData?.membership?.role === "admin";
  const isOwner = orgData?.membership?.role === "owner";

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#F4F4F5] flex items-center gap-3">
              <Users className="w-7 h-7 text-[#34C759]" />
              Mi Equipo
            </h1>
            <p className="text-[#71717A] mt-1">
              Gestiona los usuarios de tu organización
            </p>
          </div>
          
          {orgData?.has_organization && canManage && orgData?.limits?.can_invite && (
            <Button
              onClick={() => setShowInviteDialog(true)}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
              data-testid="invite-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invitar Usuario
            </Button>
          )}
        </div>

        {/* No Organization State */}
        {!orgData?.has_organization && (
          <Card className="bg-[#111111] border-[#333]">
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-[#71717A] mb-4" />
              <h3 className="text-xl font-semibold text-[#F4F4F5] mb-2">
                No tienes una organización
              </h3>
              <p className="text-[#71717A] mb-6 max-w-md mx-auto">
                Crea una organización para invitar a otros usuarios a tu equipo.
                Requiere plan Enterprise.
              </p>
              
              {user?.subscription_plan === "enterprise" || user?.subscription_plan === "enterprise_annual" ? (
                <Button
                  onClick={() => setShowCreateOrgDialog(true)}
                  className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Crear Organización
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Tu plan actual no incluye esta funcionalidad
                  </p>
                  <Button
                    onClick={() => window.location.href = "/subscription"}
                    variant="outline"
                    className="border-[#34C759] text-[#34C759]"
                  >
                    Actualizar a Enterprise
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Organization Info */}
        {orgData?.has_organization && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-[#111111] border-[#333]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#71717A] text-sm">Organización</p>
                      <p className="text-xl font-bold text-[#F4F4F5]">
                        {orgData.organization?.name}
                      </p>
                    </div>
                    <Building2 className="w-8 h-8 text-[#34C759]" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#111111] border-[#333]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#71717A] text-sm">Miembros</p>
                      <p className="text-xl font-bold text-[#F4F4F5]">
                        {orgData.limits?.current_users} / {orgData.limits?.max_users}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#111111] border-[#333]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#71717A] text-sm">Tu Rol</p>
                      <p className="text-xl font-bold text-[#F4F4F5]">
                        {roleLabels[orgData.membership?.role]}
                      </p>
                    </div>
                    {(() => {
                      const Icon = roleIcons[orgData.membership?.role] || User;
                      return <Icon className="w-8 h-8 text-yellow-400" />;
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Team Members */}
            <Card className="bg-[#111111] border-[#333]">
              <CardHeader>
                <CardTitle className="text-[#F4F4F5] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#34C759]" />
                  Miembros del Equipo
                </CardTitle>
                <CardDescription>
                  {orgData.members?.length} miembros activos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orgData.members?.map((member) => {
                    const Icon = roleIcons[member.role] || User;
                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#222]"
                        data-testid={`member-${member.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                            <Icon className={`w-5 h-5 ${
                              member.role === "owner" ? "text-yellow-400" :
                              member.role === "admin" ? "text-blue-400" : "text-gray-400"
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-[#F4F4F5]">
                              {member.user?.name}
                              {member.user_id === user?.id && (
                                <span className="text-[#71717A] text-sm ml-2">(Tú)</span>
                              )}
                            </p>
                            <p className="text-sm text-[#71717A]">{member.user?.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Badge className={roleColors[member.role]}>
                            {roleLabels[member.role]}
                          </Badge>
                          
                          {isOwner && member.role !== "owner" && (
                            <div className="flex items-center gap-2">
                              <Select
                                value={member.role}
                                onValueChange={(value) => handleChangeRole(member.id, value)}
                              >
                                <SelectTrigger className="w-32 bg-[#1A1A1A] border-[#333]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="member">Miembro</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                onClick={() => setMemberToDelete(member)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Pending Invitations */}
            {canManage && orgData.pending_invitations?.length > 0 && (
              <Card className="bg-[#111111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-[#F4F4F5] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    Invitaciones Pendientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {orgData.pending_invitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-yellow-500/20"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <p className="font-medium text-[#F4F4F5]">{invitation.email}</p>
                            <p className="text-sm text-[#71717A]">
                              Invitado como {roleLabels[invitation.role]}
                            </p>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => setInvitationToCancel(invitation)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
        <DialogContent className="bg-[#111111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5]">Crear Organización</DialogTitle>
            <DialogDescription>
              Crea tu organización para empezar a invitar usuarios a tu equipo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#F4F4F5]">Nombre de la Organización</Label>
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Ej: Mi Empresa de Audio"
                className="mt-2 bg-[#0A0A0A] border-[#333]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateOrgDialog(false)}
              className="border-[#333]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateOrganization}
              disabled={creating}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                "Crear Organización"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-[#111111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5]">Invitar Usuario</DialogTitle>
            <DialogDescription>
              Envía una invitación por email para unirse a tu equipo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#F4F4F5]">Email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="usuario@email.com"
                className="mt-2 bg-[#0A0A0A] border-[#333]"
              />
            </div>
            
            <div>
              <Label className="text-[#F4F4F5]">Rol</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger className="mt-2 bg-[#0A0A0A] border-[#333]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Miembro - Acceso básico
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin - Puede invitar usuarios
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              className="border-[#333]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleInviteUser}
              disabled={inviting}
              className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
            >
              {inviting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Invitación
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Member Confirmation */}
      <AlertDialog open={!!memberToDelete} onOpenChange={() => setMemberToDelete(null)}>
        <AlertDialogContent className="bg-[#111111] border-[#333]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5]">
              ¿Eliminar miembro?
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar a {memberToDelete?.user?.name} del equipo?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#333]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invitation Confirmation */}
      <AlertDialog open={!!invitationToCancel} onOpenChange={() => setInvitationToCancel(null)}>
        <AlertDialogContent className="bg-[#111111] border-[#333]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5]">
              ¿Cancelar invitación?
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de cancelar la invitación a {invitationToCancel?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#333]">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvitation}
              className="bg-red-600 hover:bg-red-700"
            >
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
